import React, { useState, useEffect, useRef, useCallback } from "react";
import { CircularProgress, Modal } from "@mui/material";
import { useFormik } from "formik";
import toast from "react-hot-toast";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import dayjs from "dayjs";
import "./FestivalPostView.scss";
import ProtectedComponent from "../../../../../components/ProtectedComponent";
import NavigationLayout from "../../../navigation";
import BackArrowIcon from "../../../../../components/icons/BackArrowIcon";
import Loading from "../../../../loading/Loading";
import { TemplatesApi } from "../../../../../utils/api/Templates";
import { clientRoutes } from "../../../../../utils/routes";
import { CommonEnums } from "../../../../../enums/common.enums";
import { CommonHelpers } from "../../../../../utils/helpers/common.helpers";
import UpdateTemplateModal from "../../../WorkspaceSheduleConfigure/UpdateTemplateModal";
import PreviewModal from "../../propertypost/view/PreviewModal";
import { PropertyApi } from "../../../../../utils/api/Property";
import Upload from "../../../../../components/icons/Upload";
import FestivalPostConfiguration from "./FestivalPostConfiguration";
import FestivalviewMenuIcon from "./FestivalviewMenuIcon";
import imageCompression from "browser-image-compression";
import { AuthHelpers } from "../../../../../utils/auth.helpers";

interface InputField {
  handler: string;
  label: string;
  value: string;
  template_input_index: number;
  characters_limit?: number;
  input_type?: string;
  enabled?: boolean;
  title_input?: boolean;
  use_stage_name?: boolean;
  filepath?: string;
  use_main_image?: boolean;
}

interface FestivalPostData {
  _id: string;
  post_status: string;
  post_type: string;
  schedule_datetime: string | null;
  createdAt: string;
  updatedAt: string;
  inputs: Record<string, InputField>;
  design_template_id: any;
  festival_type: string;
  listing_type?: string;
  image_post_design_activity_ref?: any;
  story_post_design_activity_ref?: any;
  video_post_design_activity_ref?: any;
  reel_post_design_activity_ref?: any;
}

const API_BASE_URL = process.env.REACT_APP_API_ENDPOINT;

const FestivalPostView = () => {
  const navigate = useNavigate();
  const { festivalPostId } = useParams<{ festivalPostId: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState("info");
  const [festivalPost, setFestivalPost] = useState<FestivalPostData | null>(
    null
  );
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isUpdateTemplateOpen, setIsUpdateTemplateOpen] = useState(false);
  const [processingModalOpen, setProcessingModalOpen] = useState(false);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [previewImages, setPreviewImages] = useState<string[]>([]);
  const [isGenerating, setIsGenerating] = useState(false);
  const [templateData, setTemplateData] = useState<any>(null);
  const [inputs, setInputs] = useState<Record<string, any>>({});
  const [formValues, setFormValues] = useState<Record<string, any>>({});
  const [initialFormValues, setInitialFormValues] = useState<
    Record<string, any>
  >({});
  const [inputOrder, setInputOrder] = useState<string[]>([]);
  const [imageEditing, setImageEditing] = useState<Record<string, boolean>>({});
  const [draggedOverKey, setDraggedOverKey] = useState<string | null>(null);
  const [isImageLoading, setIsImageLoading] = useState<Record<string, boolean>>(
    {}
  );
  const [festivalTypeName, setFestivalTypeName] = useState<string>("-");
  const fileInputRefs = useRef<Record<string, HTMLInputElement | null>>({});

  const handleFileUpdate = (file: File | undefined, key: string) => {
    if (!file || !file.type.startsWith("image/")) {
      toast.error("Please select or drop a valid image file.");
      setDraggedOverKey(null);
      return;
    }

    setIsImageLoading((prev) => ({ ...prev, [key]: true }));

    setTimeout(() => {
      const url = URL.createObjectURL(file);
      setFormValues((prev) => ({
        ...prev,
        [key]: { ...prev[key], value: url, file, original_path: "" },
      }));

      setImageEditing((prev) => ({ ...prev, [key]: false }));
      setDraggedOverKey(null);
      setIsImageLoading((prev) => ({ ...prev, [key]: false }));
    }, 500);
  };

  const fetchTemplateAndInputs = useCallback(async () => {
    if (!festivalPostId) return;
    setLoading(true);
    try {
      const res = await PropertyApi.GetFestivalPostById(festivalPostId);
      if (!res?.data?.success) {
        throw new Error(res?.data?.message || "Failed to load festival post");
      }

      const post = res.data.data;
      setFestivalPost(post);

      const subscriptionType =
        searchParams.get("subscription_type") || "agent_marketing";
      const listingType = searchParams.get("listing_type") || post.listing_type;
      const postType = searchParams.get("post_type") || post.post_type;

      const designTemplateListingType =
        post?.design_template_id?.listing_type || listingType;

      if (!searchParams.has("listing_type") && designTemplateListingType) {
        setSearchParams(
          {
            subscription_type: subscriptionType,
            post_type: postType || "",
            listing_type: designTemplateListingType || "",
          },
          { replace: true }
        );
      }

      const designTemplateId =
        post.design_template_id?._id || post.design_template_id;

      if (!designTemplateId) {
        throw new Error("Design template ID not found.");
      }

      const designTemplateRes = await TemplatesApi.getDesignTemplateById(
        designTemplateId
      );

      if (!designTemplateRes.success || !designTemplateRes.data) {
        throw new Error("Could not fetch template details.");
      }

      const templateDetails = designTemplateRes.data.data;
      setTemplateData({ data: templateDetails });

      const userRole = AuthHelpers.getUserType();
      let rolePostTypeKey = "";

      if (userRole === "agent") {
        rolePostTypeKey =
          postType === "image_post"
            ? "agent_image_template_inputs"
            : "agent_story_template_inputs";
      } else {
        rolePostTypeKey =
          postType === "image_post"
            ? "agency_image_template_inputs"
            : "agency_story_template_inputs";
      }

      const apiInputs = templateDetails[rolePostTypeKey] || {};
      const existingInputs = post.inputs || {};

      const sortedInputKeys = Object.keys(apiInputs).sort((a, b) => {
        const indexA = apiInputs[a]?.template_input_index ?? 999;
        const indexB = apiInputs[b]?.template_input_index ?? 999;
        return indexA - indexB;
      });

      const newFormValues: { [key: string]: any } = {};

      sortedInputKeys.forEach((key) => {
        const apiInput = apiInputs[key];
        const savedInput = existingInputs[key];
        const isImage = apiInput.input_type === "image";

        let value: any = "";
        let original_path: string | null = null;

        if (isImage) {
          original_path = savedInput?.filepath ?? apiInput.filepath ?? null;
          if (original_path) {
            value = CommonHelpers.getImageUrlFromImageTool({
              path: original_path,
            });
          }
        } else {
          value = savedInput?.value ?? apiInput.value ?? "";
        }

        newFormValues[key] = {
          ...apiInput,
          value,
          original_path,
          file: null,
          handler: apiInput.handler || key,
          label: apiInput.label || key,
          template_input_index: apiInput.template_input_index ?? 0,
          characters_limit: apiInput.characters_limit || 100,
          input_type: apiInput.input_type || "text",
          enabled: savedInput?.enabled ?? apiInput.enabled ?? true,
          use_main_image: isImage ? apiInput.use_main_image ?? false : false,
        };
      });

      setInputs(apiInputs);
      setFormValues(newFormValues);
      setInitialFormValues(JSON.parse(JSON.stringify(newFormValues)));
      setInputOrder(sortedInputKeys);

      // 5. Handle Generated Image Preview
      const designRef =
        post.image_post_design_activity_ref ||
        post.story_post_design_activity_ref ||
        post.video_post_design_activity_ref ||
        post.reel_post_design_activity_ref;

      if (designRef?.design_task_id?.output_image_path) {
        const existingGeneratedImage = CommonHelpers.getImageUrlFromImageTool({
          path: designRef.design_task_id.output_image_path,
        });
        setTemplateData((prev: any) => ({
          ...prev,
          generatedImage: existingGeneratedImage,
        }));
      }
    } catch (err: any) {
      setError(err.message || "An unexpected error occurred.");
      toast.error(err.message || "An unexpected error occurred.");
    } finally {
      setLoading(false);
    }
  }, [festivalPostId, searchParams, setSearchParams]);

  useEffect(() => {
    fetchTemplateAndInputs();
  }, [fetchTemplateAndInputs]);

  const buildInputsPayload = async () => {
    const imageKeysWithFiles = Object.keys(formValues).filter(
      (key) => formValues[key]?.input_type === "image" && formValues[key]?.file
    );

    const uploadedFilePaths: Record<string, string> = {};

    if (imageKeysWithFiles.length > 0) {
      const uploadEndpoint = `${process.env.REACT_APP_DESIGN_TOOL_ENDPOINT}/api/uploads/upload-template-input-images`;
      const formData = new FormData();

      const processInputImage = async (key: string) => {
        console.log("Processing image with key:", key);

        const fileObj = formValues[key].file;
        if (!fileObj) return;

        const imageInputData = formValues[key];
        const inputLabel = imageInputData?.label || key;
        const filename = fileObj.name;
        const mimetype = fileObj.type;

        const options = {
          // Uncomment these to enable compression tuning
          // maxSizeMB: 1,
          // maxWidthOrHeight: 1024,
          useWebWorker: true,
          fileType: mimetype,
        };

        try {
          const compressedFile = await imageCompression(fileObj, options);
          if (compressedFile) {
            formData.append("input_image", compressedFile, filename);
            console.log(`Successfully processed ${inputLabel}`);
          }
        } catch (error) {
          console.error("❌ Error during compression:", error);
          toast.error(`Image upload failed for: ${inputLabel}`);
        }

        console.log("Completed process for image with key: ", key);
      };

      await Promise.all(
        imageKeysWithFiles.map((key) => processInputImage(key))
      );

      try {
        const response = await fetch(uploadEndpoint, {
          method: "POST",
          body: formData,
        });

        const data = await response.json();

        if (
          data?.success &&
          Array.isArray(data.data) &&
          data.data.length === imageKeysWithFiles.length
        ) {
          imageKeysWithFiles.forEach((key, index) => {
            const filePathData = data.data[index];
            if (filePathData?.filepath) {
              uploadedFilePaths[key] = filePathData.filepath;
            }
          });
          console.log("✅ Uploaded filepaths:", uploadedFilePaths);
        } else {
          throw new Error("Bulk image upload failed.");
        }
      } catch (uploadErr) {
        console.error("Image upload failed:", uploadErr);
        toast.error("Failed to upload image(s). Please try again.");
        return null;
      }
    }

    const inputsPayload: Record<string, any> = {};
    Object.keys(formValues).forEach((key) => {
      const val = formValues[key];
      if (!val) return;

      const originalInput = inputs[key] || val;
      if (!originalInput) return;

      const isImage = val.input_type === "image";

      if (isImage) {
        const hasNewFile = !!val.file;
        const imagePayload: any = {
          ...originalInput,
          handler: val.handler || key,
          label: val.label || key,
          template_input_index: val.template_input_index ?? 0,
          input_type: "image",
          enabled: val.enabled ?? true,
          use_main_image: !hasNewFile && (val.use_main_image ?? false),
        };

        if (hasNewFile) {
          imagePayload.filepath = uploadedFilePaths[key] || "";
        } else {
          imagePayload.filepath = val.original_path || "";
        }

        delete imagePayload.value;
        delete imagePayload.file;
        delete imagePayload.original_path;

        inputsPayload[key] = imagePayload;
      } else {
        inputsPayload[key] = {
          ...originalInput,
          handler: val.handler || key,
          label: val.label || key,
          value: val.value || "",
          template_input_index: val.template_input_index ?? 0,
          characters_limit: val.characters_limit ?? 100,
          input_type: val.input_type || "text",
          enabled: val.enabled ?? true,
          file: undefined,
          original_path: undefined,
        };
      }
    });

    return inputsPayload;
  };

  const handleUpdateAndGenerateDesign = async () => {
    if (!festivalPostId) {
      toast.error("Announcement Post ID is missing!");
      return;
    }

    setProcessingModalOpen(true);
    setUpdating(true);

    try {
      const inputsPayload = await buildInputsPayload();
      if (!inputsPayload) {
        setProcessingModalOpen(false);
        setUpdating(false);
        return;
      }

      const payload = {
        inputs: inputsPayload,
        finalize: true,
      };

      const res = await PropertyApi.updateFestivalPost(payload, festivalPostId);

      if (res?.error || !res?.data?.success) {
        throw new Error(
          res?.error ||
            res?.data?.message ||
            "Failed to update and generate design."
        );
      }

      setProcessingModalOpen(false);
      setUpdating(false);
      setIsGenerating(true);

      const updatedPost = await PropertyApi.GetFestivalPostById(festivalPostId);
      if (updatedPost?.data?.success) {
        setFestivalPost(updatedPost.data.data);
      }

      const pollInterval = 3000;
      const pollDesignStatus = async () => {
        try {
          const check = await PropertyApi.GetFestivalPostById(festivalPostId);
          const festivalData = check?.data?.data;
          if (!festivalData) {
            setTimeout(pollDesignStatus, pollInterval);
            return;
          }

          setFestivalPost(festivalData);

          const designRef =
            festivalData.image_post_design_activity_ref ||
            festivalData.story_post_design_activity_ref ||
            festivalData.video_post_design_activity_ref ||
            festivalData.reel_post_design_activity_ref;

          if (!designRef?.design_task_id?.output_image_path) {
            setTimeout(pollDesignStatus, pollInterval);
            return;
          }

          const assetUrl = CommonHelpers.getImageUrlFromImageTool({
            path: designRef.design_task_id.output_image_path,
          });

          setTemplateData((prev: any) => ({
            ...prev,
            generatedImage: assetUrl,
          }));

          setIsGenerating(false);
          toast.success("New design generated successfully!");
        } catch (err) {
          console.error("Polling error:", err);
          setTimeout(pollDesignStatus, pollInterval);
        }
      };
      pollDesignStatus();
    } catch (err: any) {
      toast.error(
        err.message || "An error occurred during the update process."
      );
      setProcessingModalOpen(false);
      setUpdating(false);
      setIsGenerating(false);
    }
  };

  const getStatusClass = (post: any) => {
    if (!post) return "";

    const status = post.post_status ? post.post_status.toLowerCase() : "";

    if (status === "cancelled") return "cancelled";
    if (status === "errored" || status === "error") return "errored";

    if (post.schedule_datetime) {
      return "scheduled";
    }

    switch (status) {
      case "completed":
        return "completed";
      case "processing":
        return "processing";
      case "pending_for_social_media_upload":
        return "pending_for_social_media_upload";
      default:
        return "";
    }
  };

  const formatPostStatus = (status?: string): string => {
    if (!status) return "-";
    return status
      .split("_")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  };

  const getPostStatusDisplay = (post: any) => {
    if (!post) return "-";
    if (post.post_status === "cancelled") return "Cancelled";
    if (post.schedule_datetime) return "Scheduled to Post";
    const designRef =
      post.image_post_design_activity_ref ||
      post.story_post_design_activity_ref;
    if (designRef?.design_status === "processing") return "Processing...";
    if (designRef?.design_status === "pending_for_social_media_upload")
      return "Pending Upload";
    return formatPostStatus(post.post_status || "-");
  };

  const getSelectedTemplateId = () =>
    festivalPost?.image_post_design_activity_ref?.design_template_id?._id ||
    festivalPost?.story_post_design_activity_ref?.design_template_id?._id ||
    festivalPost?.design_template_id?._id ||
    "";

  const getPreviewContainerClass = (postType?: string) => {
    switch (postType) {
      case "story_post":
        return "story-post-preview";
      case "video_post":
        return "video-post-preview";
      case "reel_post":
        return "reel-post-preview";
      default:
        return "image-post-preview";
    }
  };

  const resetInputs = () => {
    window.location.reload();
  };

  const getDisplayImage = () => {
    if (templateData?.generatedImage) {
      return templateData.generatedImage;
    }

    const refs = [
      festivalPost?.image_post_design_activity_ref,
      festivalPost?.story_post_design_activity_ref,
      festivalPost?.video_post_design_activity_ref,
      festivalPost?.reel_post_design_activity_ref,
    ].filter(Boolean);

    const firstWithImage = refs.find(
      (ref) => ref?.design_task_id?.output_image_path
    );

    if (firstWithImage) {
      return CommonHelpers.getImageUrlFromImageTool({
        path: firstWithImage.design_task_id.output_image_path,
      });
    }

    if (templateData?.data?.template_image) {
      return `${API_BASE_URL}/${templateData.data.template_image}`;
    }

    return "/images/image-video-generation-error-fallback-image-for-card.png";
  };

  const capitalizeFirstLetter = (str: string) => {
    if (!str) return "-";
    return str
      .split("_")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
      month: "long",
      day: "numeric",
      year: "numeric",
    });
  };

  useEffect(() => {
    const fetchFestivalTypeName = async () => {
      try {
        if (!festivalPost?.festival_type) return;

        const res = await TemplatesApi.getFestivalTypes({
          search: "",
          page: 1,
          limit: 200,
          created_at: "desc",
        });

        if (res?.data?.success) {
          const list = res.data.data?.docs || res.data.data || [];

          const matchedFestival = list.find(
            (item: any) => item.slug === festivalPost.festival_type
          );

          setFestivalTypeName(
            matchedFestival?.name ||
              festivalPost.festival_type
                .split("-")
                .map(
                  (word: string) => word.charAt(0).toUpperCase() + word.slice(1)
                )
                .join(" ")
          );
        }
      } catch (error) {
        console.error("Failed to fetch festival types", error);
        setFestivalTypeName("-");
      }
    };

    fetchFestivalTypeName();
  }, [festivalPost?.festival_type]);

  return (
    <ProtectedComponent>
      <NavigationLayout>
        <div className="template-inputs-wrapper">
          <div className="top-header-bar">
            <div
              className="agent-view-back-btn"
              onClick={() => {
                const subscriptionType = searchParams.get("subscription_type");
                const backUrl = subscriptionType
                  ? `${clientRoutes.FestivalPostList}?subscription_type=${subscriptionType}`
                  : clientRoutes.FestivalPostList;

                navigate(backUrl);
              }}
            >
              <div className="back-icon">
                <BackArrowIcon />
              </div>
              <p className="bakc-text-contain">Back to List</p>
            </div>
          </div>

          <div className="header-row">
            <div className="page-title">Festival Post</div>
            <div className="action-menu-icon-wrapper">
              {festivalPost?._id && (
                <FestivalviewMenuIcon
                  postId={festivalPost._id}
                  onUpdated={fetchTemplateAndInputs}
                  onPreview={() => {
                    const imageToPreview = getDisplayImage();
                    if (
                      imageToPreview &&
                      !imageToPreview.includes("fallback")
                    ) {
                      setPreviewImages([imageToPreview]);
                      setIsPreviewOpen(true);
                    } else {
                      toast.error("No preview image available.");
                    }
                  }}
                  previewImageUrl={getDisplayImage() || ""}
                />
              )}
            </div>
          </div>

          {loading || !festivalPost ? (
            <div className="list-page-loading">
              <Loading />
            </div>
          ) : (
            <>
              <div className="festival-top-info">
                <div className="top-row">
                  <div className="info-item">
                    <label>Festival Post Id</label>
                    <p>{festivalPost._id}</p>
                  </div>
                  <div className="info-item">
                    <label>Status</label>
                    <p
                      className={`status-badge ${getStatusClass(festivalPost)}`}
                    >
                      {getPostStatusDisplay(festivalPost)}
                    </p>
                  </div>
                  <div className="info-item">
                    <label>Festival Type</label>
                    <p>{festivalTypeName}</p>
                  </div>
                  <div className="info-item">
                    <label>Post Type</label>
                    <p>{formatPostStatus(festivalPost.post_type)}</p>
                  </div>
                </div>

                <div className="bottom-row">
                  <div className="info-item">
                    <label>Schedule Datetime</label>
                    <p>
                      {festivalPost.schedule_datetime
                        ? formatDate(festivalPost.schedule_datetime)
                        : "-"}
                    </p>
                  </div>
                  <div className="info-item">
                    <label>Created On</label>
                    <p>{formatDate(festivalPost.createdAt)}</p>
                  </div>
                  <div className="info-item">
                    <label>Last Updated On</label>
                    <p>{formatDate(festivalPost.updatedAt)}</p>
                  </div>
                </div>
              </div>

              <div className="custom-tabs">
                <div
                  className={`tab-item ${activeTab === "info" ? "active" : ""}`}
                  onClick={() => setActiveTab("info")}
                >
                  Info
                </div>
                <div
                  className={`tab-item ${
                    activeTab === "postConfig" ? "active" : ""
                  }`}
                  onClick={() => setActiveTab("postConfig")}
                >
                  Post Configuration
                </div>
              </div>

              {activeTab === "info" && (
                <>
                  {loading ? (
                    <Loading />
                  ) : error ? (
                    <div className="error-message-container">{error}</div>
                  ) : (
                    <>
                      <div className="template-inputs-grid">
                        <div className="template-input-fields">
                          <div className="section-heading">Inputs</div>
                          <div className="input-description">
                            Feel free to update the inputs to customize the
                            festival post
                          </div>

                          {(() => {
                            const specificImageOrder = [
                              "bg_image",
                              "placeholder_image_top",
                              "placeholder_image_bottom",
                            ];
                            const imageInputs = inputOrder.filter(
                              (key) => inputs[key]?.input_type === "image"
                            );
                            const textInputs = inputOrder.filter(
                              (key) => inputs[key]?.input_type !== "image"
                            );
                            const orderedInputs = [
                              ...imageInputs.sort(
                                (a, b) =>
                                  specificImageOrder.indexOf(a) -
                                  specificImageOrder.indexOf(b)
                              ),
                              ...textInputs,
                            ];

                            return orderedInputs.map((key) => {
                              const input = inputs[key] || formValues[key];
                              if (!input) return null;

                              const formValueItem = formValues[key];
                              if (!formValueItem) return null;

                              const value = formValueItem.value || "";
                              const charLimit =
                                formValueItem.characters_limit || 100;
                              const isEnabled = formValueItem.enabled ?? true;
                              const isEditing = imageEditing[key] || false;

                              if (input.input_type === "image") {
                                return (
                                  <div className="input-field-block" key={key}>
                                    <div className="image-block">
                                      <div className="employee-image-header">
                                        <label className="employee-input-label">
                                          {input.label}
                                        </label>
                                        <button
                                          className={
                                            isEditing
                                              ? "cancel-image-btn"
                                              : "change-image-btn"
                                          }
                                          type="button"
                                          onClick={() =>
                                            setImageEditing((p) => ({
                                              ...p,
                                              [key]: !p[key],
                                            }))
                                          }
                                        >
                                          {isEditing
                                            ? "Cancel"
                                            : "Change Image"}
                                        </button>
                                      </div>

                                      {!isEditing && (
                                        <>
                                          {isImageLoading[key] ? (
                                            <div className="employee-image-loading-preview">
                                              <div className="spinner"></div>
                                            </div>
                                          ) : (
                                            value && (
                                              <div className="employee-image-preview">
                                                <img
                                                  src={value}
                                                  alt={input.label}
                                                  className="employee-image"
                                                  onError={(e) => {
                                                    e.currentTarget.src =
                                                      "/images/image-video-generation-error-fallback-image-for-card.png";
                                                  }}
                                                />
                                              </div>
                                            )
                                          )}
                                        </>
                                      )}

                                      {isEditing && (
                                        <div
                                          className={`employee-image-upload ${
                                            draggedOverKey === key
                                              ? "drag-over"
                                              : ""
                                          }`}
                                          onClick={() =>
                                            fileInputRefs.current[key]?.click()
                                          }
                                          onDrop={(e) => {
                                            e.preventDefault();
                                            e.stopPropagation();
                                            handleFileUpdate(
                                              e.dataTransfer.files?.[0],
                                              key
                                            );
                                            setDraggedOverKey(null);
                                          }}
                                          onDragOver={(e) => e.preventDefault()}
                                          onDragEnter={(e) => {
                                            e.preventDefault();
                                            setDraggedOverKey(key);
                                          }}
                                          onDragLeave={(e) => {
                                            e.preventDefault();
                                            setDraggedOverKey(null);
                                          }}
                                        >
                                          <input
                                            type="file"
                                            accept="image/*"
                                            ref={(el) =>
                                              (fileInputRefs.current[key] = el)
                                            }
                                            style={{ display: "none" }}
                                            onChange={(e) =>
                                              handleFileUpdate(
                                                e.target.files?.[0],
                                                key
                                              )
                                            }
                                          />
                                          <div className="upload-icon">
                                            <Upload />
                                          </div>
                                          <p>Drop or Select file</p>
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                );
                              }

                              return (
                                <div className="input-field-block" key={key}>
                                  <div className="input-label-row">
                                    <label className="input-label">
                                      {input.label} ({value?.length || 0}/
                                      {charLimit})
                                    </label>

                                    <label className="switch">
                                      <input
                                        type="checkbox"
                                        checked={isEnabled}
                                        onChange={(e) =>
                                          setFormValues((p) => ({
                                            ...p,
                                            [key]: {
                                              ...p[key],
                                              enabled: e.target.checked,
                                            },
                                          }))
                                        }
                                      />
                                      <span className="slider round"></span>
                                    </label>
                                  </div>

                                  <textarea
                                    className="input-textarea"
                                    maxLength={charLimit}
                                    disabled={!isEnabled}
                                    value={value}
                                    onChange={(e) =>
                                      setFormValues((p) => ({
                                        ...p,
                                        [key]: {
                                          ...p[key],
                                          value: e.target.value,
                                        },
                                      }))
                                    }
                                  />
                                </div>
                              );
                            });
                          })()}

                          <div className="action-text-buttons">
                            <span className="text-button" onClick={resetInputs}>
                              Reset Inputs
                            </span>
                            <span
                              className="text-button"
                              onClick={handleUpdateAndGenerateDesign}
                            >
                              Update & Generate New Design
                            </span>
                          </div>
                        </div>

                        <div className="template-preview">
                          <div className="section-heading">Post Preview</div>
                          <div className="template-preview-label">
                            <span>Preview of design template</span>
                            <div className="preview-actions">
                              <span
                                className="preview-link"
                                onClick={() => {
                                  const imageToPreview = getDisplayImage();
                                  if (
                                    imageToPreview &&
                                    !imageToPreview.includes("fallback")
                                  ) {
                                    setPreviewImages([imageToPreview]);
                                    setIsPreviewOpen(true);
                                  } else {
                                    toast.error("No preview image available.");
                                  }
                                }}
                              >
                                Preview
                              </span>
                              {/* <span
                                className="preview-link"
                                onClick={() => setIsUpdateTemplateOpen(true)}
                              >
                                Change Template
                              </span> */}
                            </div>
                          </div>
                          <div
                            className={`festival-image-section ${getPreviewContainerClass(
                              festivalPost?.post_type
                            )} ${isGenerating ? "loading" : ""}`}
                          >
                            {isGenerating ? (
                              <div className="post-preview-loading">
                                <div className="spinner" />
                                <h4>Your request is being processed.</h4>
                                <p>
                                  We’re working on your request! Creating a
                                  high-quality output can take a little while.
                                  We appreciate your patience.
                                </p>
                                <p className="message">
                                  Feel free to wait here, or browse other pages.
                                  We’ll send you a notification when your
                                  request is completed.
                                </p>
                              </div>
                            ) : (
                              <img
                                src={getDisplayImage()}
                                alt="Template Preview"
                                className="activity-thumbnail"
                                onError={(e) => {
                                  e.currentTarget.src =
                                    "/images/image-video-generation-error-fallback-image-for-card.png";
                                }}
                              />
                            )}
                          </div>
                        </div>
                      </div>

                      <div className="footer-divider" />
                      <div className="footer-buttons">
                        <button
                          className="btn btn-secondary-changes"
                          type="button"
                          onClick={() => navigate(-1)}
                        >
                          Discard Changes
                        </button>
                        <button
                          className="btn btn-primary"
                          type="button"
                          onClick={handleUpdateAndGenerateDesign}
                          disabled={updating || isGenerating}
                        >
                          Save Changes
                        </button>
                      </div>
                    </>
                  )}
                </>
              )}

              {activeTab === "postConfig" && <FestivalPostConfiguration />}
            </>
          )}

          <Modal
            open={processingModalOpen}
            aria-labelledby="processing-modal"
            className="add-property-modal styled-modal"
            disableEscapeKeyDown
          >
            <div className="modal-center-wrapper">
              <div className="loading-content-wrapper">
                <div className="loading-container">
                  <div className="spinner" />
                  <p className="loading-text">
                    Processing your request
                    <br />
                    Please wait...
                  </p>
                </div>
              </div>
            </div>
          </Modal>

          <PreviewModal
            open={isPreviewOpen}
            onClose={() => setIsPreviewOpen(false)}
            images={previewImages}
          />

          {isUpdateTemplateOpen && (
            <UpdateTemplateModal
              open={isUpdateTemplateOpen}
              onClose={() => setIsUpdateTemplateOpen(false)}
              selectedDesignTemplateId={getSelectedTemplateId()}
              templateTypeId={festivalPost?.festival_type}
              subscriptionType="agent_marketing"
              postType={festivalPost?.post_type}
              listingType={festivalPost?.listing_type}
              onConfirm={async () => {
                if (!festivalPostId) {
                  return toast.error("Festival Post ID is missing");
                }
                setIsUpdateTemplateOpen(false);
                setProcessingModalOpen(true);

                try {
                  const res = await PropertyApi.updateFestivalPost(
                    { inputs: {}, finalize: true },
                    festivalPostId
                  );

                  if (res?.error) {
                    throw new Error(
                      res.error || "Failed to trigger regeneration."
                    );
                  }

                  setProcessingModalOpen(false);
                  setIsGenerating(true);
                  toast.success("Template changed. Generating new design...");

                  await fetchTemplateAndInputs();

                  const pollInterval = 3000;
                  const pollDesignStatus = async () => {
                    try {
                      const check = await PropertyApi.GetFestivalPostById(
                        festivalPostId
                      );
                      const festivalData = check?.data?.data;
                      if (!festivalData) {
                        setTimeout(pollDesignStatus, pollInterval);
                        return;
                      }

                      setFestivalPost(festivalData);

                      const designRef =
                        festivalData.image_post_design_activity_ref ||
                        festivalData.story_post_design_activity_ref ||
                        festivalData.video_post_design_activity_ref ||
                        festivalData.reel_post_design_activity_ref;

                      if (!designRef?.design_task_id?.output_image_path) {
                        setTimeout(pollDesignStatus, pollInterval);
                        return;
                      }

                      const assetUrl = CommonHelpers.getImageUrlFromImageTool({
                        path: designRef.design_task_id.output_image_path,
                      });

                      setTemplateData((prev: any) => ({
                        ...prev,
                        generatedImage: assetUrl,
                      }));
                      setIsGenerating(false);
                      toast.success("New design generated successfully!");
                    } catch (err) {
                      console.error("Polling error:", err);
                      setTimeout(pollDesignStatus, pollInterval);
                    }
                  };
                  pollDesignStatus();
                } catch (err: any) {
                  toast.error(err.message || "Failed to change template.");
                  setProcessingModalOpen(false);
                  setIsGenerating(false);
                }
              }}
            />
          )}
        </div>
      </NavigationLayout>
    </ProtectedComponent>
  );
};

export default FestivalPostView;
